if (!customElements.get('promo-bar-slider')) {
  /**
   * @class PromoBarSlider
   * @extends HTMLElement
   *
   * Animation is pure CSS (per-item @keyframes + animation-delay)
   * and runs independently of this class.
   *
   * PromoBarSlider responsibility:
   *   1. Keep aria-hidden / tabindex in sync with which slide is visually
   *      active, on the same cadence as the CSS.
   *   2. Provide the WCAG 2.2.2 pause control, pausing the CSS animation
   *      via animation-play-state through a CSS class.
   */
  class PromoBarSlider extends HTMLElement {
    #slides = [];
    #toggle = null;

    #rotationMs = 5000;
    #activeIndex = 0;
    #timer = null;
    #paused = false;

    connectedCallback() {
      this.#slides = Array.from(this.querySelectorAll('[data-ref="slide"]'));
      this.#toggle = this.querySelector('[data-ref="toggle"]');

      this.#rotationMs = (Number(this.dataset.rotationSeconds) || 5) * 1000;

      this.#toggle?.addEventListener('click', this.#onToggleClick);

      this.addEventListener('focusin', this.#onFocusIn);
      this.addEventListener('focusout', this.#onFocusOut);

      document.addEventListener('visibilitychange', this.#onVisibilityChange);

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const isMobileViewport = window.matchMedia('(max-width: 991px)').matches;

      if (this.#slides.length > 1 && !prefersReducedMotion && isMobileViewport) {
        this.#start();
      }
    }

    disconnectedCallback() {
      this.#stop();

      this.#toggle?.removeEventListener('click', this.#onToggleClick);

      this.removeEventListener('focusin', this.#onFocusIn);
      this.removeEventListener('focusout', this.#onFocusOut);

      document.removeEventListener('visibilitychange', this.#onVisibilityChange);

      this.#slides = [];
      this.#toggle = null;
    }

    #start() {
      if (this.#timer) return;
      this.#paused = false;
      this.classList.remove('is-paused');
      this.#timer = setInterval(this.#advance, this.#rotationMs);
      this.#setToggleState(false);
    }

    #stop() {
      clearInterval(this.#timer);
      this.#timer = null;
    }

    #pause() {
      if (this.#paused) return;
      this.#paused = true;
      this.classList.add('is-paused');
      this.#stop();
      this.#setToggleState(true);
    }

    #resume() {
      if (!this.#paused) return;
      this.#start();
    }

    #advance = () => {
      const next = (this.#activeIndex + 1) % this.#slides.length;
      const current = this.#slides[this.#activeIndex];
      const upcoming = this.#slides[next];

      current.setAttribute('aria-hidden', 'true');
      current.querySelectorAll('a').forEach(
        (link) => link.setAttribute('tabindex', '-1')
      );

      upcoming.removeAttribute('aria-hidden');
      upcoming.querySelectorAll('a').forEach(
        (link) => link.removeAttribute('tabindex')
      );

      this.#activeIndex = next;
    };

    #setToggleState(isPaused) {
      this.#toggle?.setAttribute('aria-pressed', String(isPaused));
    }

    #onToggleClick = () => (
      this.#paused ? this.#resume() : this.#pause()
    );

    #onFocusIn = (event) => {
      if (event.target === this.#toggle) return;
      this.#pause();
    };

    #onFocusOut = (event) => {
      if (event.target === this.#toggle) return;
      this.#resume();
    };

    #onVisibilityChange = () => (
      document.hidden ? this.#pause() : this.#resume()
    );
  }

  customElements.define('promo-bar-slider', PromoBarSlider);
}
